import { computed, defineComponent } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required, minLength, maxLength, requiredIf, minValue, helpers } from "@vuelidate/validators";
import { mapState } from "pinia";
import { Container } from "aurelia-dependency-injection";

import { enumToMap } from "../../../../../common/vue-helpers/modifiers/enum-modifiers";
import {
    ApplicationEnum,
    HospiceHispanicEthnicities,
    HospiceRaces,
    EthnicityPalliative,
    GenderIdentities,
    MaritalStatuses,
    RacesPalliative,
    SexualOrientation,
    Veterans,
    Gender,
    PhoneTypeEnum,
    TribalAffiliation,
    HospiceHispanicEthnicitiesEnum
} from "../../../../../enums/enums";
import { BranchesService } from "../../../../../services/branches-service";
import { IPhoneNumber } from "../../../../../resources-vue/vue-interfaces/i-phone-number";
import { Address } from "../../../../../resources-vue/vue-models/address";
import { formatDateInput } from "../../../../../common/vue-helpers/modifiers/value-modifier";
import { useCreateReferralHosPal } from "../../store/useCreateReferralHosPal";
import { LookupService } from "../../../../../services/lookup-service";
import { ITypeaheadOptions } from "../../../../../resources-vue/vue-interfaces/i-typeahead";
import { ToastrService } from "../../../../../services/toastr.service";

import ValidateAddress from "../../../../../resources-vue/vue-dialogs/ValidateAddress/ValidateAddress.vue";
import DatePicker from "../../../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";
import MultiSelectDropdown from "../../../../../resources-vue/vue-custom-elements/MultiSelectDropdown/MultiSelectDropdown.vue";
import PhoneNumber from "../../../../../resources-vue/vue-custom-elements/PhoneNumber/PhoneNumber.vue";
import TagsInput from "../../../../../resources-vue/vue-custom-elements/TagsInput/TagsInput.vue";
import FormSection from "../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import ReferralAddressForm from "../ReferralAddressForm/ReferralAddressForm.vue";
import TypeaheadInput from "../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import MultiSelectFilter from "../../../../../resources-vue/vue-elements/MultiSelectFilter/MultiSelectFilter.vue";

export default defineComponent({
    components: {
        FormSection,
        DatePicker,
        MultiSelectDropdown,
        PhoneNumber,
        TagsInput,
        ReferralAddressForm,
        TypeaheadInput,
        MultiSelectFilter
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef),
            v: computed(() => this.v$)
        };
    },

    validations() {
        const phoneNumber = helpers.withMessage("Primary phone number must be at least 10 digits", (phones: any[]) => {
            // this is here so that required validation is returned for empty phone number
            if (!phones?.length) {
                return true;
            }
            const primary = phones?.find((phone) => phone.isPrimary);

            return !!primary?.number?.trim() && primary.number.trim().length >= 10;
        });

        const phoneType = helpers.withMessage("Primary phone type is required", (phones: any[]) => {
            const primary = phones?.find((phone) => phone.isPrimary);

            return (primary?.type ?? 0) >= 1;
        });
        return {
            referral: {
                firstName: { required },
                lastName: { required },
                dateOfBirth: { required },
                gender: { minValue: minValue(1) },
                otherGenderIdentity: {
                    required: requiredIf(() => {
                        return this.referral.genderIdentity === 6;
                    })
                },
                otherSexualOrientation: {
                    required: requiredIf(() => {
                        return this.referral.sexualOrientation === 4;
                    })
                },
                phoneNumbers: {
                    required,
                    phoneNumber,
                    phoneType
                },
                locationId: { required },
                hispanicEthnicityValues: {
                    required: requiredIf(() => {
                        return this.isHisSection;
                    })
                },
                ethnicityOnly: {
                    required: requiredIf(() => {
                        return this.isApplicationPalliative && !this.isEthnicityPalliativeDeclined;
                    })
                },
                typeOfAffiliation: {
                    required: requiredIf(() => {
                        return this.referral.isIdentifyWithTribe === true;
                    })
                },
                race: {
                    required: requiredIf(() => {
                        return this.isHisSection;
                    })
                },
                socialSecurityNumber: { minLength: minLength(9), maxLength: maxLength(9) },
                primaryAddress: {
                    country: { required },
                    addressLine1: { required },
                    city: { required },
                    state: {
                        required: requiredIf(() => {
                            return this.isUnitedStates;
                        })
                    },
                    zipCode: { required },
                    county: {
                        required: requiredIf(() => {
                            return this.isUnitedStates;
                        })
                    }
                },
                visitAddress: {
                    country: { required },
                    addressLine1: { required },
                    city: { required },
                    state: {
                        required: requiredIf(() => {
                            return this.isUnitedStates;
                        })
                    },
                    zipCode: { required },
                    county: {
                        required: requiredIf(() => {
                            return this.isUnitedStates;
                        })
                    }
                },
                mailingAddress: {
                    country: { required },
                    addressLine1: { required },
                    city: { required },
                    state: {
                        required: requiredIf(() => {
                            return this.isUnitedStates;
                        })
                    },
                    zipCode: { required },
                    county: {
                        required: requiredIf(() => {
                            return this.isUnitedStates;
                        })
                    }
                },
                previousAddress: {
                    country: {
                        required: requiredIf(() => {
                            return this.referral.hasPreviousAddress;
                        })
                    },
                    addressLine1: {
                        required: requiredIf(() => {
                            return this.referral.hasPreviousAddress;
                        })
                    },
                    city: {
                        required: requiredIf(() => {
                            return this.referral.hasPreviousAddress;
                        })
                    },
                    state: {
                        required: requiredIf(() => {
                            return this.isUnitedStates && this.referral.hasPreviousAddress;
                        })
                    },
                    zipCode: {
                        required: requiredIf(() => {
                            return this.referral.hasPreviousAddress;
                        })
                    },
                    county: {
                        required: requiredIf(() => {
                            return this.isUnitedStates && this.referral.hasPreviousAddress;
                        })
                    }
                }
            }
        };
    },

    props: {
        isHisSection: {
            type: Boolean,
            default: true
        },

        isPatient: {
            type: Boolean,
            default: true
        },
        providerId: {
            type: String,
            default: ""
        },
        isRefer: {
            type: Boolean,
            default: false
        },
        isCreate: {
            type: Boolean,
            default: true
        }
    },
    data(vm) {
        return {
            filter: "",
            disabled: false,
            id: "granular-race",
            granularRace: null,
            availableRace: [],
            occupation: null,
            industry: null,
            _lookupService: null,
            tribeList: null,
            availableTribeName: [],
            tribe: "" as string,
            showotherTribalAffiliation: false,
            isFocus: false,
            isOpen: false,
            displayData: [],
            loading: true,
            isAuthorized: true,
            dialogRef: null,
            _toastrService: null as ToastrService,
            _branchesService: null,
            _visitAddressCache: null,
            _visitAddressFacilityCache: null,
            _mailingAddressCache: null,
            _mailingAddressFacilityCache: null,
            mailingAddrSameAsPrimaryAddr: false,
            visitAddrSameAsPrimaryAddr: true,
            isVeteranSelected: false,
            selectedRacesHospice: [],
            selectedEthnicityHospice: [],
            selectedRacesPalliative: [],
            isRacesPalliativeDeclined: false,
            isEthnicityPalliativeDeclined: false,
            hasDeclinedToIdentifyTribe: null,
            branches: null,
            phoneNumberTypeOptionsConfig: [PhoneTypeEnum.Home, PhoneTypeEnum.Mobile, PhoneTypeEnum.Facility],
            isAddressValidationInProgress: false,
            middleName: "",
            referralAddressFormRef: null,
            Genders: Array.from(Gender.enumWithDescriptions.values()),
            MaritalStatuses: enumToMap(MaritalStatuses),
            EthnicityPalliative: enumToMap(EthnicityPalliative),
            RacesHospice: Array.from(HospiceRaces.enumWithDescriptions.values()),
            EthnicityHospice: Array.from(HospiceHispanicEthnicities.enumWithDescriptions.values()),
            RacesPalliative: Array.from(RacesPalliative.enumWithDescriptions.values()),
            TribalAffiliation: enumToMap(TribalAffiliation),
            Veterans: Array.from(Veterans.enumWithDescriptions.values()),
            SexualOrientation: enumToMap(SexualOrientation),
            GenderIdentities: enumToMap(GenderIdentities),
            genderUnknownValue: Gender.enum.Unknown,
            formatDateInput,
            enumToMap,
            v$: useVuelidate(),
            tooltip:
                "For emergency preparedness, patient will be contacted at this number. This will also populate to the emergency preparedness report."
        };
    },
    created() {
        this._branchesService = Container.instance.get(BranchesService);
        this._lookupService = Container.instance.get(LookupService);
        this._toastrService = Container.instance.get(ToastrService);
    },

    async mounted() {
        this.loading = true;
        await this.initOptions();
        this.initFields();
    },

    computed: {
        ...mapState(useCreateReferralHosPal, ["referral"]),
        veteranOptions() {
            return this.Veterans?.map((option) => ({
                name: option.name,
                value: option.value
            }));
        },
        isApplicationPalliative(): boolean {
            return this.referral.application == ApplicationEnum.AxxessPalliative;
        },
        middleLabel(): string {
            return this.isApplicationPalliative ? "Middle Name" : "M.I.";
        },
        arePhoneNumbersAvailable() {
            return this.referral.phoneNumbers.length;
        },
        primaryNumber: {
            get() {
                return this.referral.phoneNumbers?.[this.primaryNumberIndex(this.referral.phoneNumbers)]?.number;
            },
            set(value: any) {
                this.referral.phoneNumbers[this.primaryNumberIndex(this.referral.phoneNumbers)].number = value;
            }
        },
        primaryExtension: {
            get() {
                return this.referral.phoneNumbers?.[this.primaryNumberIndex(this.referral.phoneNumbers)]?.extension;
            },
            set(value: any) {
                this.referral.phoneNumbers[this.primaryNumberIndex(this.referral.phoneNumbers)].extension = value;
            }
        },
        primaryType: {
            get() {
                const value = this.referral.phoneNumbers?.[this.primaryNumberIndex(this.referral.phoneNumbers)]?.type;
                return value ?? (this.isRefer ? 0 : 1);
            },
            set(value: any) {
                this.referral.phoneNumbers[this.primaryNumberIndex(this.referral.phoneNumbers)].type = value;
            }
        },
        primaryCountry: {
            get() {
                return this.referral.phoneNumbers?.[this.primaryNumberIndex(this.referral.phoneNumbers)]?.country;
            },
            set(value: any) {
                return (this.referral.phoneNumbers[this.primaryNumberIndex(this.referral.phoneNumbers)].country =
                    value);
            }
        },
        alternateNumber: {
            get() {
                return this.referral.phoneNumbers?.[this.alternativeNumberIndex(this.referral.phoneNumbers)]?.number;
            },
            set(value: any) {
                this.referral.phoneNumbers[this.alternativeNumberIndex(this.referral.phoneNumbers)].number = value;
            }
        },
        alternateExtension: {
            get() {
                return this.referral.phoneNumbers?.[this.alternativeNumberIndex(this.referral.phoneNumbers)]?.extension;
            },
            set(value: any) {
                this.referral.phoneNumbers[this.alternativeNumberIndex(this.referral.phoneNumbers)].extension = value;
            }
        },
        alternateType: {
            get() {
                const value =
                    this.referral.phoneNumbers?.[this.alternativeNumberIndex(this.referral.phoneNumbers)]?.type;
                return value ? value : this.isRefer ? 0 : 1;
            },
            set(value: any) {
                this.referral.phoneNumbers[this.alternativeNumberIndex(this.referral.phoneNumbers)].type = value;
            }
        },
        alternateCountry: {
            get() {
                return this.referral.phoneNumbers?.[this.alternativeNumberIndex(this.referral.phoneNumbers)]?.country;
            },
            set(value: any) {
                this.referral.phoneNumbers[this.alternativeNumberIndex(this.referral.phoneNumbers)].country = value;
            }
        },
        isUnitedStates() {
            return this.referral.primaryAddress.country === "840";
        },
        multipleAddressChanges() {
            return JSON.stringify({
                a: this.referral.primaryAddress?.addressLine1,
                b: this.referral.primaryAddress?.city,
                c: this.referral.primaryAddress?.state,
                d: this.referral.primaryAddress?.zipCode,
                e: this.referral.primaryAddress?.county,
                f: this.referral.primaryAddress?.addressLine2,
                g: this.referral.primaryAddress?.country
            });
        },
        isValidAddress() {
            return (
                this.referral?.primaryAddress?.latitude !== null && this.referral?.primaryAddress?.longitude !== null
            );
        }
    },
    watch: {
        mailingAddrSameAsPrimaryAddr(newValue: boolean) {
            this.mailingAddrSameAsPrimaryAddrChanged(newValue);
        },
        visitAddrSameAsPrimaryAddr(newValue: boolean) {
            this.visitAddrSameAsPrimaryAddrChanged(newValue);
        },
        isVeteranSelected(newValue: boolean) {
            if (!!this.referral && !newValue) {
                this.referral.veteran = [];
            }
        },
        isEthnicityPalliativeDeclined(newValue: boolean) {
            this.ethnicityPalliativeDeclined(newValue);
        },
        isRacesPalliativeDeclined(newValue: boolean) {
            this.racesPalliativeDeclined(newValue);
        },
        multipleAddressChanges: {
            handler() {
                if (this.referral.primaryAddress.isValidatedAndByPassToggled) {
                    this.referral.primaryAddress.longitude = null;
                    this.referral.primaryAddress.latitude = null;
                } else {
                    this.referral.primaryAddress.isValidatedAndByPassToggled = true;
                }
                this.handlePrimaryAddressChange();
            },
            immediate: false,
            deep: false
        },
        middleName(newValue) {
            if (this.isApplicationPalliative) {
                this.referral.middleName = newValue;
            } else {
                this.referral.middleInitial = newValue;
            }
        },
        "referral.isIdentifyWithTribe"(newValue) {
            if (newValue !== null) {
                this.referral.hasDeclinedToIdentifyTribe = false;
            }
            if (newValue === false) {
                this.tribeList = [];
                this.referral.tribalAffiliations = [];
                this.referral.typeOfAffiliation = 0;
            }
        },
        isValidAddress: {
            handler() {
                if (this.isValidAddress === true) {
                    if (
                        !this.referral.primaryAddress?.addressLine1 ||
                        !this.referral.primaryAddress?.city ||
                        !this.referral.primaryAddress?.state ||
                        !this.referral.primaryAddress?.zipCode ||
                        !this.referral.primaryAddress?.country
                    ) {
                        this.referral.primaryAddress.longitude = null;
                        this.referral.primaryAddress.latitude = null;
                    }
                }
            },
            immediate: false,
            deep: false
        }
    },
    methods: {
        async initOptions() {
            try {
                const branches = await this._branchesService.getAllBranches();
                this.branches = branches.filter(
                    (branch: any) =>
                        branch.accountId == this.referral.accountId && branch.application == this.referral.application
                );
                if (this.isRefer) {
                    this.phoneNumberTypeOptionsConfig.unshift(PhoneTypeEnum.None);
                }
                const res = await this._lookupService.getGranularRacePal(ApplicationEnum.AxxessPalliative);
                if (res.isSuccess) {
                    this.availableRace = res.data?.map((race: any) => ({
                        name: race.DisplayName,
                        value: race.Code
                    }));
                }
            } catch (e) {
                console.log(e);
            }
        },
        initFields() {
            if (!!this.referral) {
                this.selectedRacesHospice = this.RacesHospice.filter((race) => {
                    return this.referral.race?.some((item) => {
                        return race.value == item;
                    });
                }) as any;
                this.selectedEthnicityHospice = this.EthnicityHospice.filter((ethnicity) => {
                    return this.referral.hispanicEthnicityValues?.some((item) => {
                        return ethnicity.value == item;
                    });
                });
                this.selectedRacesPalliative = this.RacesPalliative.filter((race) => {
                    return this.referral.race?.some((item: any) => {
                        return race.value == item;
                    });
                }) as any;
                if (!this.referral.previousAddress) {
                    this.referral.previousAddress = new Address();
                } else {
                    this.referral.hasPreviousAddress = !!this.referral.previousAddress.addressLine1;
                }
                this._mailingAddressCache = new Address(this.referral.mailingAddress);
                this._mailingAddressFacilityCache = this.referral.mailingAddressFacilityTypeId;
                this.mailingAddrSameAsPrimaryAddr = this.referral.isMailingAddressSameAsPrimary;
                this._visitAddressCache = new Address(this.referral.visitAddress);
                this._visitAddressFacilityCache = this.referral.visitAddressFacilityTypeId;

                if (
                    this.referral.isVisitAddressSameAsPrimary !== undefined &&
                    this.referral.isVisitAddressSameAsPrimary !== null
                ) {
                    this.visitAddrSameAsPrimaryAddr = this.referral.isVisitAddressSameAsPrimary;
                } else {
                    this.visitAddrSameAsPrimaryAddr =
                        this.referral.visitAddress?.addressLine1 === this.referral.primaryAddress?.addressLine1;
                }
                if (this.referral.veteran?.length > 0) {
                    this.isVeteranSelected = true;
                }
                if (this.primaryNumberIndex(this.referral.phoneNumbers) < 0) {
                    this.referral.phoneNumbers = this.referral.phoneNumbers.concat({
                        number: "",
                        extension: "",
                        type: 1,
                        country: null,
                        isPrimary: true
                    });
                }
                if (this.alternativeNumberIndex(this.referral.phoneNumbers) < 0) {
                    this.referral.phoneNumbers = this.referral.phoneNumbers.concat({
                        number: "",
                        extension: "",
                        type: 1,
                        country: null,
                        isPrimary: false
                    });
                }
                this.isRacesPalliativeDeclined = this.referral.declineToSpecifyRace;
                this.isEthnicityPalliativeDeclined = this.referral.declineToSpecifyEthnicity;
                this.occupation = this.referral.occupation
                    ? {
                          name: this.referral.occupation.DisplayName,
                          value: {
                              Code: this.referral.occupation.Code,
                              CodeSystem: this.referral.occupation.CodeSystem,
                              CodeSystemName: this.referral.occupation.CodeSystemName,
                              DisplayName: this.referral.occupation.DisplayName
                          }
                      }
                    : null;
                this.industry = this.referral.industry
                    ? {
                          name: this.referral.industry.DisplayName,
                          value: {
                              Code: this.referral.industry.Code,
                              CodeSystem: this.referral.industry.CodeSystem,
                              CodeSystemName: this.referral.industry.CodeSystemName,
                              DisplayName: this.referral.industry.DisplayName
                          }
                      }
                    : null;
            }
            if (this.referral.tribalAffiliations?.length) {
                this.tribeList = this.referral.tribalAffiliations.map((opt) => ({
                    name: opt.display,
                    value: opt.code
                }));
            }
            if (this.referral.raceCode) {
                this.granularRace = this.availableRace.find((race) => race.value == this.referral.raceCode)?.name;
            }
            if (this.isApplicationPalliative) {
                this.middleName = this.referral.middleName;
            } else {
                this.middleName = this.referral.middleInitial;
            }
            if (this.isCreate) {
                this.referral.cahpsSurveyContactType = 0;
            }
            this.loading = false;
        },

        moreGranularRaceFilter(filter: string) {
            if (filter) {
                const filterAvailableRaces = this.availableRace.filter((race: any) => {
                    return race.name.toLowerCase().includes(filter.toLowerCase());
                });
                return filterAvailableRaces;
            } else {
                return this.availableRace;
            }
        },
        granularRaceChanged(newValue: any) {
            this.granularRace = newValue;
            this.referral.raceCode = newValue.value;
        },
        async tribeNameFilter(filter: string) {
            try {
                const payload = {
                    application: ApplicationEnum.AxxessPalliative,
                    typeOfAffiliation: this.referral.typeOfAffiliation,
                    searchTerm: filter,
                    limit: 25
                };
                const resp = await this._lookupService.getTribalAffiliation(payload);
                const result = resp.data
                    .map((tribe: any) => ({
                        name: tribe.Name,
                        value: tribe.Code
                    }))
                    .filter((x) => !filter || x.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase()));
                return result;
            } catch (e) {
                console.log(e);
            }
        },
        async tribeListChanged(options: ITypeaheadOptions[]) {
            this.tribeList = options;
            this.referral.tribalAffiliations = options?.map((opt) => ({
                display: opt.name,
                code: opt.value
            }));
        },

        hasPreviousAddressChanged() {
            if (!!this.referral && !this.referral.hasPreviousAddress) {
                this.referral.previousAddress = new Address();
                this.referral.previousAddressFacilityTypeId = null;
            }
        },
        handlePrimaryAddressChange() {
            if (this.mailingAddrSameAsPrimaryAddr) {
                this.referral.mailingAddress = new Address(this.referral.primaryAddress);
                this.referral.mailingAddressFacilityTypeId = this.referral.primaryAddressFacilityTypeId;
            }
            if (this.visitAddrSameAsPrimaryAddr) {
                this.referral.visitAddress = new Address(this.referral.primaryAddress);
                this.referral.visitAddressFacilityTypeId = this.referral.primaryAddressFacilityTypeId;
            }
        },

        racesPalliativeDeclined(newValue: boolean) {
            if (newValue == true) {
                this.referral.race = [];
                this.selectedRacesPalliative = [];
            }
            if (!!this.referral) {
                this.referral.declineToSpecifyRace = this.isRacesPalliativeDeclined;
            }
        },

        ethnicityPalliativeDeclined(newValue: boolean) {
            if (newValue == true) {
                this.referral.ethnicityOnly = null;
            }
            if (!!this.referral) {
                this.referral.declineToSpecifyEthnicity = this.isEthnicityPalliativeDeclined;
            }
        },

        primaryNumberIndex(phoneNumbers: IPhoneNumber[]) {
            if (Array.isArray(phoneNumbers) && phoneNumbers?.length) {
                return phoneNumbers.findIndex((e) => e.isPrimary);
            }
            return 0;
        },
        alternativeNumberIndex(phoneNumbers: IPhoneNumber[]) {
            if (Array.isArray(phoneNumbers) && phoneNumbers.length) {
                return phoneNumbers.findIndex((e) => !e.isPrimary);
            }
            return 0;
        },
        resetSameAsPrimaryAddress() {
            if (this.referral.isVisitAddressSameAsPrimary) {
                this.visitAddrSameAsPrimaryAddrChanged(true);
            }
            if (this.referral.isMailingAddressSameAsPrimary) {
                this.mailingAddrSameAsPrimaryAddrChanged(true);
            }
        },
        mailingAddrSameAsPrimaryAddrChanged(newValue: boolean) {
            if (!!this.referral) {
                if (newValue === true) {
                    this.referral.isMailingAddressSameAsPrimary = true;
                    this.referral.mailingAddress = new Address(this.referral.primaryAddress);
                    this.referral.mailingAddressFacilityTypeId = this.referral.primaryAddressFacilityTypeId;
                } else {
                    this.referral.isMailingAddressSameAsPrimary = false;
                    if (this._mailingAddressCache) {
                        this.referral.mailingAddress = new Address(this._mailingAddressCache);
                        this.referral.mailingAddressFacilityTypeId = this._mailingAddressFacilityCache;
                    } else {
                        this.referral.mailingAddress = new Address();
                        this.referral.mailingAddressFacilityTypeId = null;
                    }
                }
            }
        },
        visitAddrSameAsPrimaryAddrChanged(newValue: boolean) {
            if (!!this.referral) {
                if (newValue === true) {
                    this.referral.isVisitAddressSameAsPrimary = true;
                    this.referral.visitAddress = new Address(this.referral.primaryAddress);
                    this.referral.visitAddressFacilityTypeId = this.referral.primaryAddressFacilityTypeId;
                } else {
                    this.referral.isVisitAddressSameAsPrimary = false;
                    if (this._visitAddressCache) {
                        this.referral.visitAddress = new Address(this._visitAddressCache);
                        this.referral.visitAddressFacilityTypeId = this._visitAddressFacilityCache;
                    } else {
                        this.referral.visitAddress = new Address();
                        this.referral.visitAddressFacilityTypeId = null;
                    }
                }
            }
        },

        getDate(date: any, name: string) {
            if (name === "dateOfBirth") {
                this.referral.dateOfBirth = date;
            }
        },
        handleVeteran() {
            this.isVeteranSelected = !this.isVeteranSelected;
        },
        selectedItemsUpdated(value: any) {
            if (this.referral.veteran.includes(value)) {
                const index = this.referral.veteran.indexOf(value);
                this.referral.veteran.splice(index, 1);
                return;
            }
            this.referral.veteran.push(value);
        },

        updateSelectedHospiceRaces(value: any) {
            this.selectedRacesHospice = value;
            this.referral.race = this.selectedRacesHospice.map((race) => Number(race.value));
        },
        updateSelectedHospiceEthnictiy(value: any) {
            const values = value.map((ethnicity) => Number(ethnicity.value));
            let isError = false;
            if (
                values.includes(HospiceHispanicEthnicitiesEnum.NoNotOfHispanicLatinoOrSpanishOrigin) &&
                (values.includes(HospiceHispanicEthnicitiesEnum.YesMexicanAmericanChicano) ||
                    values.includes(HospiceHispanicEthnicitiesEnum.YesPuertoRican) ||
                    values.includes(HospiceHispanicEthnicitiesEnum.YesCuban) ||
                    values.includes(HospiceHispanicEthnicitiesEnum.YesAnotherHispanicLatinoOrSpanishOrigin) ||
                    values.includes(HospiceHispanicEthnicitiesEnum.PatientDeclinesToRespond))
            ) {
                isError = true;
            } else if (
                values.includes(HospiceHispanicEthnicitiesEnum.PatientUnableToRespond) &&
                values.includes(HospiceHispanicEthnicitiesEnum.PatientDeclinesToRespond)
            ) {
                isError = true;
            } else if (values.includes(HospiceHispanicEthnicitiesEnum.PatientDeclinesToRespond) && values.length > 1) {
                isError = true;
            }
            if (isError) {
                this._toastrService.error({
                    title: "Error",
                    message:
                        "Invalid Combination for HOPE Ethnicity: Y must be selected alone; A cannot be combined with B-E or Y; B-E cannot be combined with Y; X cannot be combined with Y."
                });
            }
            this.selectedEthnicityHospice = value;
            this.referral.hispanicEthnicityValues = this.selectedEthnicityHospice.map((ethnicity) =>
                Number(ethnicity.value)
            );
        },
        updateSelectedRaces(value: any) {
            this.selectedRacesPalliative = value;
            this.referral.race = this.selectedRacesPalliative.map((race) => Number(race.value));
        },
        async validateAddress() {
            let model = {
                originalAddress: {
                    addressLine1: this.referral.primaryAddress.addressLine1,
                    addressLine2: this.referral.primaryAddress.addressLine2,
                    city: this.referral.primaryAddress.city,
                    state: this.referral.primaryAddress.state,
                    zipCode: this.referral.primaryAddress.zipCode,
                    county: this.referral.primaryAddress.county,
                    country: this.referral.primaryAddress.country,
                    latitude: this.referral.primaryAddress.latitude,
                    longitude: this.referral.primaryAddress.longitude
                }
            };
            this.isAddressValidationInProgress = true;
            let updatedAddress = null;
            this.dialogRef = this.$dialog.open(ValidateAddress, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    model
                },
                onClose: (options) => {
                    if (options.data?.success) {
                        updatedAddress = options.data.updatedAddress;
                        this.referral.primaryAddress.addressLine1 = updatedAddress.addressLine1;
                        this.referral.primaryAddress.addressLine2 = updatedAddress.addressLine2;
                        this.referral.primaryAddress.city = updatedAddress.city;
                        this.referral.primaryAddress.state = updatedAddress.state;
                        this.referral.primaryAddress.zipCode = updatedAddress.zipCode;
                        this.referral.primaryAddress.county = updatedAddress.county;
                        this.referral.primaryAddress.latitude = updatedAddress.latitude;
                        this.referral.primaryAddress.longitude = updatedAddress.longitude;
                        this.referral.primaryAddress.isValidated = true;
                        this.referral.primaryAddress.isBypassed = false;
                        if (updatedAddress.country) {
                            this.referral.primaryAddress.country = updatedAddress.country;
                        }
                    } else {
                        this.referral.primaryAddress.isValidated = false;
                        this.referral.primaryAddress.isBypassed = true;
                    }
                    this.referral.primaryAddress.isValidatedAndByPassToggled = false;
                }
            });
            this.isAddressValidationInProgress = false;
        },
        async getOccupationList(filter: string) {
            const payload = {
                searchTerm: filter,
                application: ApplicationEnum.AxxessPalliative
            };
            let response = await this._lookupService.getOccupation(payload);
            return response.data?.map((occupation: any) => ({
                value: occupation,
                name: occupation.DisplayName
            }));
        },

        async getIndustryList(filter: string) {
            const payload = {
                searchTerm: filter,
                application: ApplicationEnum.AxxessPalliative
            };
            let response = await this._lookupService.getIndustry(payload);
            return response.data?.map((industry: any) => ({
                value: industry,
                name: industry.DisplayName
            }));
        },

        occupationChanged(newValue: any) {
            this.occupation = newValue;
            if (newValue) {
                this.referral.occupation = {
                    Code: newValue.value.Code,
                    CodeSystem: newValue.value.CodeSystem,
                    CodeSystemName: newValue.value.CodeSystemName,
                    DisplayName: newValue.value.DisplayName
                };
            }
        },
        industryChanged(newValue: any) {
            this.industry = newValue;
            if (newValue) {
                this.referral.industry = {
                    Code: newValue.value.Code,
                    CodeSystem: newValue.value.CodeSystem,
                    CodeSystemName: newValue.value.CodeSystemName,
                    DisplayName: newValue.value.DisplayName
                };
            }
        },
        handleDeclineChange(isChecked: boolean): void {
            if (isChecked) {
                this.referral.isIdentifyWithTribe = null;
                this.referral.hasDeclinedToIdentifyTribe = true;
                this.tribeList = [];
                this.referral.tribalAffiliations = [];
                this.referral.typeOfAffiliation = 0;
            } else {
                this.referral.hasDeclinedToIdentifyTribe = false;
            }
        }
    }
});
